/**
 * hooks/useProviderConnection/withTimeout.ts
 * Generic timeout wrapper with AbortSignal priority
 *
 * Mirrors SyncInitializationService.withTimeout pattern.
 * Pure function with zero dependencies - perfect for unit testing.
 */

/**
 * Wraps a promise with timeout protection.
 *
 * Features:
 * - AbortSignal priority: Abort takes precedence over timeout
 * - Cleanup guarantee: Timer and listeners cleared on all exit paths
 * - Once listener: Abort listener auto-removes after first invocation
 * - Error categorization: Distinguishes 'TIMEOUT' from 'ABORTED' via Error message
 *
 * @param promise - Promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param signal - Optional AbortSignal for manual cancellation
 * @returns Promise that rejects on timeout or abort
 *
 * @example
 * ```ts
 * const controller = new AbortController();
 * const result = await withTimeout(
 *   fetch('/api/data'),
 *   5000,
 *   controller.signal
 * );
 * ```
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // Check if already aborted (abort takes priority)
    if (signal?.aborted) {
      reject(new Error('ABORTED'));
      return;
    }

    const timer = setTimeout(() => reject(new Error('TIMEOUT')), timeoutMs);

    // Cleanup helper (clears timer and removes abort listener)
    const settle = (fn: () => void): void => {
      clearTimeout(timer);
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
      fn();
    };

    // Abort handler (abort takes priority over timeout)
    const onAbort = (): void => settle(() => reject(new Error('ABORTED')));

    // Attach abort listener
    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }

    // Settle on promise resolution/rejection
    promise.then(
      (value) => settle(() => resolve(value)),
      (error) => settle(() => reject(error))
    );
  });
}
